import { useEffect, useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const ContactPage = () => {
  const formAnimation = useScrollAnimation();
  const infoAnimation = useScrollAnimation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiry_type: '',
    message: '',
    privacy_agree: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
    
    document.title = '岡山市英会話教室へのお問い合わせ｜無料体験レッスン受付中｜おかやま英会話教室株式会社';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '岡山市の英会話教室へのお問い合わせはこちら。無料体験レッスンのお申し込みも受付中。電話、FAX、お問い合わせフォームからお気軽にご連絡ください。');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '岡山英会話教室問い合わせ,無料体験レッスン,英会話教室申込,岡山市英語スクール連絡先');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${siteUrl}/contact`);
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${siteUrl}/contact`);
    }
    
    // Add Schema.org JSON-LD for Contact page
    const existingSchema = document.querySelector('script[type="application/ld+json"][data-page-schema]');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "mainEntity": {
        "@type": "EducationalOrganization",
        "name": "おかやま英会話教室株式会社",
        "url": `${siteUrl}/`,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "○○町1-2-3",
          "addressLocality": "岡山市北区",
          "addressRegion": "岡山県",
          "postalCode": "700-0000",
          "addressCountry": "JP"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "34.6617",
          "longitude": "133.9350"
        },
        "telephone": "+81-86-012-3456",
        "faxNumber": "+81-86-012-3457",
        "email": "info@okayama-english.jp",
        "openingHoursSpecification": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      }
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-schema', 'true');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.inquiry_type || !formData.message) {
      alert('必須項目をすべて入力してください。');
      return;
    }

    if (!formData.privacy_agree) {
      alert('プライバシーポリシーに同意してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('inquiry_type', formData.inquiry_type);
      formBody.append('message', formData.message);
      formBody.append('privacy_agree', formData.privacy_agree ? 'true' : 'false');

      const response = await fetch('https://readdy.ai/api/form/d5kug3hubuu1ag7lenl0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          inquiry_type: '',
          message: '',
          privacy_agree: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center mt-20">
        <img
          src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/8691fc9fef1272e5a23b582a9f01cf22.jpeg"
          alt="お問い合わせ"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        
        <div className="relative z-10 text-center text-navy-900 max-w-4xl mx-auto px-4">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Contact
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed">
            お気軽にご相談ください
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-white">
        <div 
          ref={formAnimation.ref}
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${formAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="text-center mb-12">
            <p className="text-gray-600 text-lg">
              無料体験レッスンのご予約、コースに関するご質問など、<br />
              お気軽にお問い合わせください。
            </p>
          </div>
          
          <form id="contact-form" onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
            <div>
              <label htmlFor="name" className="block text-navy-900 font-medium mb-2">
                お名前 <span className="text-coral-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-mint-500 transition-colors"
                placeholder="山田 太郎"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-navy-900 font-medium mb-2">
                メールアドレス <span className="text-coral-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-mint-500 transition-colors"
                placeholder="example@email.com"
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-navy-900 font-medium mb-2">
                電話番号
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-mint-500 transition-colors"
                placeholder="090-1234-5678"
              />
            </div>
            
            <div>
              <label htmlFor="inquiry_type" className="block text-navy-900 font-medium mb-2">
                お問い合わせ種別 <span className="text-coral-500">*</span>
              </label>
              <select
                id="inquiry_type"
                name="inquiry_type"
                value={formData.inquiry_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-mint-500 transition-colors cursor-pointer"
              >
                <option value="">選択してください</option>
                <option value="無料体験レッスン">無料体験レッスン</option>
                <option value="コース詳細">コース詳細</option>
                <option value="料金について">料金について</option>
                <option value="その他">その他</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="message" className="block text-navy-900 font-medium mb-2">
                お問い合わせ内容 <span className="text-coral-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-mint-500 transition-colors resize-none"
                placeholder="お問い合わせ内容をご記入ください（500文字以内）"
              ></textarea>
              <p className="text-sm text-gray-500 mt-2 text-right">
                {formData.message.length} / 500文字
              </p>
            </div>
            
            <div className="flex items-start">
              <input
                type="checkbox"
                id="privacy_agree"
                name="privacy_agree"
                checked={formData.privacy_agree}
                onChange={handleChange}
                required
                className="mt-1 mr-3 w-5 h-5 cursor-pointer"
              />
              <label htmlFor="privacy_agree" className="text-gray-700 text-sm cursor-pointer">
                <a href="#" className="text-mint-500 hover:underline">プライバシーポリシー</a>
                に同意します <span className="text-coral-500">*</span>
              </label>
            </div>
            
            {submitStatus === 'success' && (
              <div className="bg-mint-500/10 border border-mint-500 text-mint-700 px-6 py-4 rounded-lg">
                <i className="ri-check-line mr-2"></i>
                お問い合わせを受け付けました。ご連絡ありがとうございます。
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="bg-coral-500/10 border border-coral-500 text-coral-700 px-6 py-4 rounded-lg">
                <i className="ri-error-warning-line mr-2"></i>
                送信に失敗しました。もう一度お試しください。
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-coral-500 hover:bg-coral-600 text-white py-4 rounded-lg text-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? '送信中...' : '送信する'}
            </button>
          </form>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20 bg-beige-50">
        <div 
          ref={infoAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${infoAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-coral-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-phone-line text-3xl text-coral-500"></i>
              </div>
              <h3 className="font-bold text-xl text-navy-900 mb-4">電話でのお問い合わせ</h3>
              <p className="text-2xl font-bold text-coral-500 mb-2">086-012-3456</p>
              <p className="text-gray-600 text-sm">受付時間: 9:00 - 18:00</p>
              <p className="text-gray-600 text-sm">定休日: 日曜日・祝日</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-mint-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-3xl text-mint-500"></i>
              </div>
              <h3 className="font-bold text-xl text-navy-900 mb-4">メールでのお問い合わせ</h3>
              <p className="text-lg font-medium text-navy-900 mb-2">info@okayama-english.jp</p>
              <p className="text-gray-600 text-sm">24時間受付</p>
              <p className="text-gray-600 text-sm">返信目安: 1営業日以内</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-navy-900/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-3xl text-navy-900"></i>
              </div>
              <h3 className="font-bold text-xl text-navy-900 mb-4">教室へのアクセス</h3>
              <p className="text-gray-700 mb-2">岡山市北区○○町1-2-3</p>
              <p className="text-gray-600 text-sm">JR岡山駅より徒歩10分</p>
              <p className="text-gray-600 text-sm">専用駐車場10台完備</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ContactPage;